import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';
import { addToStopList } from './productsSlice';

// Интерфейс для товара в технических заданиях
export interface TechnicalSpecProduct {
  id: string;
  name: string;
  url: string;
  thumb_url?: string;
  thumb_middle_url?: string;
  subject_id: number;
  subject_name: string;
  country?: string;
  desc: string;
  eng_name?: string;
  eng_desc?: string;
  tz_confirmed: boolean;
  image_url_1?: string;
  image_url_2?: string;
  image_url_3?: string;
  image_url_4?: string;
  article?: string;
  season?: string;
  created_at?: string;
  updated_at?: string;
  package_summary?: string;
  stop_list?: boolean;
  source_code?: string;
  source_title?: string;
  source_color?: string;
}

// Интерфейс для фильтров
export interface TechnicalSpecsFilters {
  search: string;
  subject_id: string;
  country: string;
  exclude_phrase: string;
  sort_field: string;
  sort_direction: 'ASC' | 'DESC';
  /** По умолчанию true: показывать только не подтверждённые ТЗ */
  tz_unconfirmed_only: boolean;
  /** По умолчанию true: исключать товары из стоплиста из списка */
  exclude_stop_list: boolean;
  source_code: string;
}

// Интерфейс для пагинации
export interface TechnicalSpecsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// Интерфейс для состояния
export interface TechnicalSpecsState {
  products: TechnicalSpecProduct[];
  newWaveProducts: TechnicalSpecProduct[];
  loading: boolean;
  newWaveLoading: boolean;
  error: string | null;
  filters: TechnicalSpecsFilters;
  newWaveFilters: TechnicalSpecsFilters;
  pagination: TechnicalSpecsPagination;
  newWavePagination: TechnicalSpecsPagination;
  activeTab: 'regular' | 'newWave';
  listRequestId: string | null;
  newWaveListRequestId: string | null;
}

// Начальное состояние
const initialState: TechnicalSpecsState = {
  products: [],
  newWaveProducts: [],
  loading: false,
  newWaveLoading: false,
  error: null,
  filters: {
    search: '',
    subject_id: '',
    country: '',
    exclude_phrase: '',
    sort_field: 'product_name',
    sort_direction: 'ASC',
    tz_unconfirmed_only: true,
    exclude_stop_list: true,
    source_code: ''
  },
  newWaveFilters: {
    search: '',
    subject_id: '',
    country: '',
    exclude_phrase: '',
    sort_field: 'product_name',
    sort_direction: 'ASC',
    tz_unconfirmed_only: true,
    exclude_stop_list: true,
    source_code: ''
  },
  pagination: {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  },
  newWavePagination: {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  },
  activeTab: 'regular',
  listRequestId: null,
  newWaveListRequestId: null,
};

// Async thunks
export const fetchTechnicalSpecs = createAsyncThunk(
  'technicalSpecs/fetchTechnicalSpecs',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { technicalSpecs: TechnicalSpecsState };
      const { filters, pagination } = state.technicalSpecs;
      
      // Формируем фильтры, исключая пустые значения
      const filtersToSend = {
        status_id: '4b8e80b8-28d9-4bc8-9fe8-262dcfe5814d', // Фиксированный статус "Расчет ЮЭ"
        ...(filters.search && { search: filters.search }),
        ...(filters.subject_id && { subject_id: filters.subject_id }),
        ...(filters.country && { country: filters.country }),
        ...(filters.exclude_phrase && { exclude_phrase: filters.exclude_phrase }),
        ...(filters.sort_field && { sort_field: filters.sort_field }),
        ...(filters.sort_direction && { sort_direction: filters.sort_direction }),
        ...(filters.tz_unconfirmed_only && { tz_unconfirmed: 'true' }),
        ...(filters.exclude_stop_list !== undefined && { exclude_stop_list: filters.exclude_stop_list }),
        ...(filters.source_code && { source_code: filters.source_code })
      };

      const response = await api.getTechnicalSpecs(pagination.page, pagination.limit, filtersToSend);
      console.log('API response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('fetchTechnicalSpecs error:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch technical specs');
    }
  }
);

// Async thunk для New Wave товаров
export const fetchTechnicalSpecsNewWave = createAsyncThunk(
  'technicalSpecs/fetchTechnicalSpecsNewWave',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { technicalSpecs: TechnicalSpecsState };
      const { newWaveFilters, newWavePagination } = state.technicalSpecs;
      
      // Формируем фильтры, исключая пустые значения
      const filtersToSend = {
        status_id: '4b8e80b8-28d9-4bc8-9fe8-262dcfe5814d', // Фиксированный статус "Расчет ЮЭ"
        ...(newWaveFilters.search && { search: newWaveFilters.search }),
        ...(newWaveFilters.subject_id && { subject_id: newWaveFilters.subject_id }),
        ...(newWaveFilters.country && { country: newWaveFilters.country }),
        ...(newWaveFilters.exclude_phrase && { exclude_phrase: newWaveFilters.exclude_phrase }),
        ...(newWaveFilters.sort_field && { sort_field: newWaveFilters.sort_field }),
        ...(newWaveFilters.sort_direction && { sort_direction: newWaveFilters.sort_direction }),
        ...(newWaveFilters.tz_unconfirmed_only && { tz_unconfirmed: 'true' }),
        ...(newWaveFilters.exclude_stop_list !== undefined && { exclude_stop_list: newWaveFilters.exclude_stop_list }),
        ...(newWaveFilters.source_code && { source_code: newWaveFilters.source_code })
      };

      const response = await api.getTechnicalSpecsNewWave(newWavePagination.page, newWavePagination.limit, filtersToSend);
      console.log('API response for New Wave:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('fetchTechnicalSpecsNewWave error:', error);
      console.error('Error response:', error.response);
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch technical specs new wave');
    }
  }
);

export const updateDescription = createAsyncThunk(
  'technicalSpecs/updateDescription',
  async ({ productId, desc }: { productId: string; desc: string }, { rejectWithValue }) => {
    try {
      const response = await api.updateTechnicalSpec(productId, desc);
      return { productId, desc };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update description');
    }
  }
);

export const updateProductName = createAsyncThunk(
  'technicalSpecs/updateProductName',
  async ({ productId, name }: { productId: string; name: string }, { rejectWithValue }) => {
    try {
      const response = await api.updateProduct(productId, { name });
      return { productId, name };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update product name');
    }
  }
);

export const updateEnglishName = createAsyncThunk(
  'technicalSpecs/updateEnglishName',
  async ({ productId, eng_name }: { productId: string; eng_name: string }, { rejectWithValue }) => {
    try {
      const response = await api.updateTechnicalSpecFields(productId, { eng_name });
      return { productId, eng_name };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update English name');
    }
  }
);

export const updateEnglishDesc = createAsyncThunk(
  'technicalSpecs/updateEnglishDesc',
  async ({ productId, eng_desc }: { productId: string; eng_desc: string }, { rejectWithValue }) => {
    try {
      const response = await api.updateTechnicalSpecFields(productId, { eng_desc });
      return { productId, eng_desc };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update English description');
    }
  }
);

export const updateSeason = createAsyncThunk(
  'technicalSpecs/updateSeason',
  async ({ productId, season }: { productId: string; season: string }, { rejectWithValue }) => {
    try {
      const response = await api.updateTechnicalSpecFields(productId, { season });
      return { productId, season };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update season');
    }
  }
);

export const updateSeasonBatch = createAsyncThunk(
  'technicalSpecs/updateSeasonBatch',
  async (
    { productIds, season }: { productIds: string[]; season: string },
    { rejectWithValue }
  ) => {
    try {
      await Promise.all(
        productIds.map((productId) =>
          api.updateTechnicalSpecFields(productId, { season })
        )
      );
      return { productIds, season };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update season batch');
    }
  }
);

export const confirmTZ = createAsyncThunk(
  'technicalSpecs/confirmTZ',
  async ({ productId, confirmed, desc, eng_name, eng_desc }: { productId: string; confirmed: boolean; desc?: string; eng_name?: string; eng_desc?: string }, { rejectWithValue }) => {
    try {
      const response = await api.confirmTZFull(productId, { confirmed, desc, eng_name, eng_desc });
      return { productId, confirmed, desc, eng_name, eng_desc };
    } catch (error: any) {
      // Если есть ошибки валидации, передаем их вместе с сообщением
      if (error.response?.status === 400 && error.response?.data?.validationErrors) {
        return rejectWithValue({
          message: error.response?.data?.error || 'Validation failed',
          validationErrors: error.response.data.validationErrors
        });
      }
      return rejectWithValue({
        message: error.response?.data?.error || 'Failed to confirm TZ',
        validationErrors: undefined
      });
    }
  }
);

export const uploadImage = createAsyncThunk(
  'technicalSpecs/uploadImage',
  async ({ productId, imageFile, imageIndex }: { productId: string; imageFile: File; imageIndex: number }, { rejectWithValue }) => {
    try {
      const response = await api.uploadImage(productId, imageFile, imageIndex);
      return {
        productId,
        imageUrl: response.data.imageUrl,
        imageIndex,
        thumbMiddleUrl: response.data.thumbMiddleUrl as string | null | undefined
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to upload image');
    }
  }
);

export const deleteImage = createAsyncThunk(
  'technicalSpecs/deleteImage',
  async ({ productId, imageIndex }: { productId: string; imageIndex: number }, { rejectWithValue }) => {
    try {
      const response = await api.deleteImage(productId, imageIndex);
      return { productId, imageIndex };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete image');
    }
  }
);

export const swapImages = createAsyncThunk(
  'technicalSpecs/swapImages',
  async ({ productId, fromIndex, toIndex }: { productId: string; fromIndex: number; toIndex: number }, { rejectWithValue }) => {
    try {
      const response = await api.swapImages(productId, fromIndex, toIndex);
      return { productId, fromIndex, toIndex };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to swap images');
    }
  }
);

export const generateDescription = createAsyncThunk(
  'technicalSpecs/generateDescription',
  async ({ productId, templateId, jobId, onStep }: { productId: string; templateId?: string; jobId?: string; onStep?: (step: string) => void }, { rejectWithValue }) => {
    try {
      const data = jobId
        ? await api.pollTzJob(productId, jobId, onStep)
        : await api.generateDescription(productId, templateId || '', onStep);
      return {
        productId,
        desc: data.desc as string | undefined,
        eng_desc: data.eng_desc as string | undefined,
        season: data.season as string | undefined,
        at: data.at as number | undefined,
      };
    } catch (error: any) {
      if (error?.name === 'TzCancelled' || error?.message === 'Отменено') return rejectWithValue('Отменено');
      return rejectWithValue(error.response?.data?.error || error.message || 'Не удалось сгенерировать');
    }
  }
);

// Slice
const technicalSpecsSlice = createSlice({
  name: 'technicalSpecs',
  initialState,
  reducers: {
    applyGeneratedText: (state, action: PayloadAction<{ productId: string; desc?: string; eng_desc?: string; season?: string }>) => {
      const { productId, desc, eng_desc, season } = action.payload;
      const apply = (product?: TechnicalSpecProduct) => {
        if (!product || String(product.id) !== String(productId)) return;
        if (desc !== undefined) product.desc = desc;
        if (eng_desc !== undefined) product.eng_desc = eng_desc;
        if (season !== undefined) product.season = season;
      };
      state.products.forEach(apply);
      state.newWaveProducts.forEach(apply);
    },
    setFilters: (state, action: PayloadAction<Partial<TechnicalSpecsFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        subject_id: '',
        country: '',
        exclude_phrase: '',
        sort_field: 'product_name',
        sort_direction: 'ASC',
        tz_unconfirmed_only: true,
        exclude_stop_list: true,
        source_code: ''
      };
      state.pagination.page = 1;
    },
    setPagination: (state, action: PayloadAction<Partial<TechnicalSpecsPagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    updatePackageSummary: (state, action: PayloadAction<{ productId: string; packageSummary: string }>) => {
      const { productId, packageSummary } = action.payload;
      const product = state.products.find(p => p.id === productId);
      if (product) {
        product.package_summary = packageSummary;
      }
      const newWaveProduct = state.newWaveProducts.find(p => p.id === productId);
      if (newWaveProduct) {
        newWaveProduct.package_summary = packageSummary;
      }
    },
    setNewWaveFilters: (state, action: PayloadAction<Partial<TechnicalSpecsFilters>>) => {
      state.newWaveFilters = { ...state.newWaveFilters, ...action.payload };
      state.newWavePagination.page = 1;
    },
    clearNewWaveFilters: (state) => {
      state.newWaveFilters = {
        search: '',
        subject_id: '',
        country: '',
        exclude_phrase: '',
        sort_field: 'product_name',
        sort_direction: 'ASC',
        tz_unconfirmed_only: true,
        exclude_stop_list: true,
        source_code: ''
      };
      state.newWavePagination.page = 1;
    },
    setNewWavePagination: (state, action: PayloadAction<Partial<TechnicalSpecsPagination>>) => {
      state.newWavePagination = { ...state.newWavePagination, ...action.payload };
    },
    setActiveTab: (state, action: PayloadAction<'regular' | 'newWave'>) => {
      state.activeTab = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Fetch technical specs
    builder
      .addCase(fetchTechnicalSpecs.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(fetchTechnicalSpecs.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        state.products = action.payload.data;
        state.pagination.total = action.payload.total;
        state.pagination.totalPages = action.payload.totalPages;
      })
      .addCase(fetchTechnicalSpecs.rejected, (state, action) => {
        if (action.meta.requestId !== state.listRequestId) return;
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch technical specs new wave
      .addCase(fetchTechnicalSpecsNewWave.pending, (state, action) => {
        state.newWaveLoading = true;
        state.error = null;
        state.newWaveListRequestId = action.meta.requestId;
      })
      .addCase(fetchTechnicalSpecsNewWave.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.newWaveListRequestId) return;
        state.newWaveLoading = false;
        state.newWaveProducts = action.payload.data || [];
        state.newWavePagination.total = action.payload.total || 0;
        state.newWavePagination.totalPages = action.payload.totalPages || 0;
      })
      .addCase(fetchTechnicalSpecsNewWave.rejected, (state, action) => {
        if (action.meta.requestId !== state.newWaveListRequestId) return;
        state.newWaveLoading = false;
        state.error = action.payload as string;
      });

    // Sync stop_list when addToStopList is fulfilled (from productsSlice)
    builder
      .addCase(addToStopList.fulfilled, (state, action) => {
        const { productId, response } = action.payload;
        if (response?.stop_list === undefined) return;
        const product = state.products.find(p => p.id === productId);
        if (product) product.stop_list = response.stop_list;
        const newWaveProduct = state.newWaveProducts.find(p => p.id === productId);
        if (newWaveProduct) newWaveProduct.stop_list = response.stop_list;
      });

    // Update description
    builder
      .addCase(updateDescription.fulfilled, (state, action) => {
        const { productId, desc } = action.payload;
        const product = state.products.find(p => p.id === productId);
        if (product) {
          product.desc = desc;
        }
        const newWaveProduct = state.newWaveProducts.find(p => p.id === productId);
        if (newWaveProduct) {
          newWaveProduct.desc = desc;
        }
      })
      .addCase(updateDescription.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Update product name
    builder
      .addCase(updateProductName.fulfilled, (state, action) => {
        const { productId, name } = action.payload;
        const product = state.products.find(p => p.id === productId);
        if (product) {
          product.name = name;
        }
        const newWaveProduct = state.newWaveProducts.find(p => p.id === productId);
        if (newWaveProduct) {
          newWaveProduct.name = name;
        }
      })
      .addCase(updateProductName.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Update English name
    builder
      .addCase(updateEnglishName.fulfilled, (state, action) => {
        const { productId, eng_name } = action.payload;
        const product = state.products.find(p => p.id === productId);
        if (product) {
          product.eng_name = eng_name;
        }
        const newWaveProduct = state.newWaveProducts.find(p => p.id === productId);
        if (newWaveProduct) {
          newWaveProduct.eng_name = eng_name;
        }
      })
      .addCase(updateEnglishName.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Update English description
    builder
      .addCase(updateEnglishDesc.fulfilled, (state, action) => {
        const { productId, eng_desc } = action.payload;
        const product = state.products.find(p => p.id === productId);
        if (product) {
          product.eng_desc = eng_desc;
        }
        const newWaveProduct = state.newWaveProducts.find(p => p.id === productId);
        if (newWaveProduct) {
          newWaveProduct.eng_desc = eng_desc;
        }
      })
      .addCase(updateEnglishDesc.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Update season
    builder
      .addCase(updateSeason.fulfilled, (state, action) => {
        const { productId, season } = action.payload;
        const product = state.products.find(p => p.id === productId);
        if (product) {
          product.season = season;
        }
        const newWaveProduct = state.newWaveProducts.find(p => p.id === productId);
        if (newWaveProduct) {
          newWaveProduct.season = season;
        }
      })
      .addCase(updateSeason.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Update season batch (one state update for all)
    builder
      .addCase(updateSeasonBatch.fulfilled, (state, action) => {
        const { productIds, season } = action.payload;
        productIds.forEach((id) => {
          const product = state.products.find((p) => p.id === id);
          if (product) product.season = season;
          const newWaveProduct = state.newWaveProducts.find((p) => p.id === id);
          if (newWaveProduct) newWaveProduct.season = season;
        });
      })
      .addCase(updateSeasonBatch.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Confirm TZ
    builder
      .addCase(confirmTZ.fulfilled, (state, action) => {
        const { productId, confirmed, desc, eng_name, eng_desc } = action.payload;
        const product = state.products.find(p => p.id === productId);
        if (product) {
          product.tz_confirmed = confirmed;
          if (desc !== undefined) product.desc = desc;
          if (eng_name !== undefined) product.eng_name = eng_name;
          if (eng_desc !== undefined) product.eng_desc = eng_desc;
        }
        const newWaveProduct = state.newWaveProducts.find(p => p.id === productId);
        if (newWaveProduct) {
          newWaveProduct.tz_confirmed = confirmed;
          if (desc !== undefined) newWaveProduct.desc = desc;
          if (eng_name !== undefined) newWaveProduct.eng_name = eng_name;
          if (eng_desc !== undefined) newWaveProduct.eng_desc = eng_desc;
        }
      })
      .addCase(confirmTZ.rejected, (state, action) => {
        // Для ошибок валидации не устанавливаем state.error, они обрабатываются в компоненте
        // Устанавливаем error только для других типов ошибок
        if (typeof action.payload === 'object' && action.payload !== null && 'message' in action.payload) {
          const payload = action.payload as { message: string; validationErrors?: any };
          // Если это ошибка валидации, не устанавливаем state.error
          if (!payload.validationErrors) {
            state.error = payload.message;
          }
          // Иначе ошибка валидации обрабатывается в компоненте через validationErrors
        } else {
        state.error = action.payload as string;
        }
      });

    // Upload image
    builder
      .addCase(uploadImage.fulfilled, (state, action) => {
        const { productId, imageUrl, imageIndex, thumbMiddleUrl } = action.payload;
        const product = state.products.find(p => p.id === productId);
        if (product) {
          if (thumbMiddleUrl) product.thumb_middle_url = thumbMiddleUrl;
          if (imageIndex === 0) {
            (product as any)['thumb_url'] = imageUrl;
          } else {
            const imageField = `image_url_${imageIndex}` as keyof TechnicalSpecProduct;
            (product as any)[imageField] = imageUrl;
          }
        }
        const newWaveProduct = state.newWaveProducts.find(p => p.id === productId);
        if (newWaveProduct) {
          if (thumbMiddleUrl) newWaveProduct.thumb_middle_url = thumbMiddleUrl;
          if (imageIndex === 0) {
            (newWaveProduct as any)['thumb_url'] = imageUrl;
          } else {
            const imageField = `image_url_${imageIndex}` as keyof TechnicalSpecProduct;
            (newWaveProduct as any)[imageField] = imageUrl;
          }
        }
      })
      .addCase(uploadImage.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete image
    builder
      .addCase(deleteImage.fulfilled, (state, action) => {
        const { productId, imageIndex } = action.payload;
        const product = state.products.find(p => p.id === productId);
        if (product) {
          if (imageIndex === 0) {
            (product as any)['thumb_url'] = undefined;
          } else {
            const imageField = `image_url_${imageIndex}` as keyof TechnicalSpecProduct;
            (product as any)[imageField] = undefined;
          }
        }
        const newWaveProduct = state.newWaveProducts.find(p => p.id === productId);
        if (newWaveProduct) {
          if (imageIndex === 0) {
            (newWaveProduct as any)['thumb_url'] = undefined;
          } else {
            const imageField = `image_url_${imageIndex}` as keyof TechnicalSpecProduct;
            (newWaveProduct as any)[imageField] = undefined;
          }
        }
      })
      .addCase(deleteImage.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Swap images
    builder
      .addCase(swapImages.fulfilled, (state, action) => {
        const { productId, fromIndex, toIndex } = action.payload;
        const product = state.products.find(p => p.id === productId);
        if (product) {
          const resolveField = (idx: number) => (idx === 0 ? 'thumb_url' : `image_url_${idx}`);
          const fromField = resolveField(fromIndex) as keyof TechnicalSpecProduct;
          const toField = resolveField(toIndex) as keyof TechnicalSpecProduct;
          const fromValue = (product as any)[fromField];
          const toValue = (product as any)[toField];

          (product as any)[fromField] = toValue;
          (product as any)[toField] = fromValue;
        }
        const newWaveProduct = state.newWaveProducts.find(p => p.id === productId);
        if (newWaveProduct) {
          const resolveField = (idx: number) => (idx === 0 ? 'thumb_url' : `image_url_${idx}`);
          const fromField = resolveField(fromIndex) as keyof TechnicalSpecProduct;
          const toField = resolveField(toIndex) as keyof TechnicalSpecProduct;
          const fromValue = (newWaveProduct as any)[fromField];
          const toValue = (newWaveProduct as any)[toField];

          (newWaveProduct as any)[fromField] = toValue;
          (newWaveProduct as any)[toField] = fromValue;
        }
      })
      .addCase(swapImages.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Generate description
    builder
      .addCase(generateDescription.fulfilled, (state, action) => {
        const { productId, desc, eng_desc, season } = action.payload;
        const apply = (product?: TechnicalSpecProduct) => {
          if (!product) return;
          if (desc !== undefined) product.desc = desc;
          if (eng_desc !== undefined) product.eng_desc = eng_desc;
          if (season !== undefined) product.season = season;
        };
        apply(state.products.find(p => String(p.id) === String(productId)));
        apply(state.newWaveProducts.find(p => String(p.id) === String(productId)));
      })
      .addCase(generateDescription.rejected, (state, action) => {
        // Не устанавливаем state.error, так как ошибка обрабатывается в компоненте
        // и показывается как предупреждение
      });
  }
});

export const { 
  applyGeneratedText,
  setFilters, 
  clearFilters, 
  setPagination, 
  clearError, 
  updatePackageSummary,
  setNewWaveFilters,
  clearNewWaveFilters,
  setNewWavePagination,
  setActiveTab
} = technicalSpecsSlice.actions;
export default technicalSpecsSlice.reducer;